import { useState } from "react";
import { useTranslation } from "react-i18next";
import { User, MoreHorizontal, Calendar, Plus, Globe, Eye, ClipboardCopy } from "lucide-react";
import { SkillBadge } from "@/components/SkillBadge";
import { MatchScore } from "@/components/MatchScore";
import { Link } from "react-router-dom";
import { KanbanCandidate } from "./types/index";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

interface KanbanColumnProps {
	id: string;
	title: string;
	candidates: KanbanCandidate[];
	onDragStart: (e: React.DragEvent, candidateId: string, sourceColumnId: string) => void;
	onDragOver: (e: React.DragEvent) => void;
	onDrop: (e: React.DragEvent, targetColumnId: string) => void;
	pointOfView?: "job" | "candidate";
	statusConfig?: Record<string, {
		color: string;
		bgColor: string;
		borderColor: string;
		headerBg: string;
		headerText: string;
		dotColor: string;
	}>;
	// Multi-select props
	isMultiSelectMode?: boolean;
	onToggleItemSelection?: (id: string) => void;
	isItemSelected?: (id: string) => boolean;
}

export const KanbanColumn = ({
	id,
	title,
	candidates,
	onDragStart,
	onDragOver,
	onDrop,
	pointOfView = "job",
	statusConfig,
	isMultiSelectMode = false,
	onToggleItemSelection,
	isItemSelected
}: KanbanColumnProps) => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [collapsed, setCollapsed] = useState(id === 'refused');
	const [isDragOver, setIsDragOver] = useState(false);
	
	// Get status-specific colors
	const statusColors = statusConfig?.[id] || statusConfig?.[title] || {
		color: 'text-gray-600 dark:text-gray-300',
		bgColor: 'bg-gray-50 dark:bg-gray-800/50',
		borderColor: 'border-gray-200 dark:border-gray-700',
		headerBg: 'bg-gray-100 dark:bg-gray-800',
		headerText: 'text-gray-800 dark:text-gray-200',
		dotColor: 'bg-gray-500'
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
		onDragOver(e);
	};

	const handleDragLeave = () => {
		setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		setIsDragOver(false);
		onDrop(e, id);
	};

	// Helper to get the two shortest tags
	const getShortestTags = (tags: string[]) => {
		if (!tags || tags.length === 0) return [];
		const sorted = [...tags].sort((a, b) => a.length - b.length);
		return sorted.slice(0, 2);
	};
	const getMoreCount = (tags: string[]) => Math.max(0, tags.length - 2);

	// Helper to get days ago string
	function getDaysAgo(dateString: string | undefined): string {
		if (!dateString) return '';
		const date = new Date(dateString);
		const now = new Date();
		const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
		if (diffDays === 0) return 'modified today';
		if (diffDays === 1) return 'modified 1 day ago';
		return `modified ${diffDays} days ago`;
	}

	return (
		<div 
			key={id}
			className={`flex-shrink-0 flex flex-col rounded-sm border border-gray-200 dark:border-gray-700 transition-all duration-300 bg-white dark:bg-gray-900 ${
				collapsed ? 'w-10 items-center min-w-[2.5rem] max-w-[2.5rem]' : 'w-72'
			}`}
			style={{
				...(isDragOver ? { outline: '2px solid #3b82f6', outlineOffset: 2, background: collapsed ? undefined : 'rgba(59,130,246,0.08)' } : {})
			}}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{collapsed ? (
				<div className="flex flex-col items-center w-full pt-2 cursor-pointer select-none" onClick={() => setCollapsed(false)}>
					<div className={`w-3 h-3 rounded-full ${statusColors.dotColor} mb-2`}></div>
					<span className="font-medium text-xs font-semibold text-gray-900 dark:text-gray-100" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{title}</span>
					<button
						className="mt-2 text-xs text-blue-500 hover:underline focus:outline-none"
						onClick={e => { e.stopPropagation(); setCollapsed(false); }}
						aria-label={t('common.expand')}
					>
						+
					</button>
					{candidates.length > 0 && (
						<div className="mt-2">
							<div className={`rounded-sm flex items-center justify-center font-bold shadow transition-all duration-300 h-6 w-6 text-[10px] ${
								isDragOver ? statusColors.dotColor : statusColors.dotColor
							} text-white`}>
								{candidates.length}
							</div>
						</div>
					)}
				</div>
			) : (
				<div className={`px-4 py-3 border-b rounded-t-sm bg-white dark:bg-gray-900 ${
					isDragOver ? 'bg-blue-50/40 dark:bg-blue-900/30' : ''
				}`} style={{borderBottom: '2px solid #e5e7eb', boxShadow: '0 2px 4px 0 rgba(0,0,0,0.03)'}}>
					<div className="flex justify-between items-center">
						<div className="flex items-center gap-2">
							<div className={`w-3 h-3 rounded-full ${statusColors.dotColor}`}></div>
							<h3 className="font-semibold text-lg tracking-tight text-gray-900 dark:text-gray-100">{title}</h3>
						</div>
						<div className="flex items-center gap-2">
							<Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
								{candidates.length}
							</Badge>
							<button
								className="ml-2 text-xs text-blue-500 hover:underline focus:outline-none"
								onClick={() => setCollapsed((c) => !c)}
								aria-label={t('common.collapse')}
							>
								–
							</button>
						</div>
					</div>
				</div>
			)}
			{!collapsed ? (
				<div className={`flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px] ${
					isDragOver ? 'bg-blue-50/20 dark:bg-blue-900/20' : ''
				}`}>
					{candidates.map(candidate => {
						const company = candidate.company;
						const jobTitle = candidate.jobTitle || "";
						const createdAt = candidate.createdAt ? new Date(candidate.createdAt) : null;
						let daysAgo = null;
						if (createdAt) {
							const now = new Date();
							const utc1 = Date.UTC(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
							const utc2 = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
							daysAgo = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
						}
						return (
							<div 
								key={candidate.id}
								className={`bg-white/95 dark:bg-black/80 backdrop-blur-sm rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm p-3 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] group w-full relative ${
									isMultiSelectMode ? 'cursor-pointer' : 'cursor-move'
								} ${isItemSelected?.(candidate.id) ? 'ring-2 ring-blue-500 bg-blue-50/50' : ''}`}
								style={{
									boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
								}}
								draggable={!isMultiSelectMode}
								onDragStart={(e) => !isMultiSelectMode && onDragStart(e, candidate.id, id)}
								onClick={(e) => {
									if (isMultiSelectMode) {
										// In multi-select mode, clicking the card selects/deselects it
										if (!(e.target as Element).closest('a, button, input[type="checkbox"]')) {
											onToggleItemSelection?.(candidate.id);
										}
									} else {
										// Only add click behavior for candidate cards (not job cards)
										// Don't trigger if clicking on a link or button
										if (candidate.candidateId && !(e.target as Element).closest('a, button')) {
											window.location.href = `/matches/${candidate.id}`;
										}
									}
								}}
							>
								{isMultiSelectMode && onToggleItemSelection && (
									<div className="absolute top-2 right-2 z-10" onClick={e => e.stopPropagation()}>
										<Checkbox
											checked={isItemSelected?.(candidate.id) || false}
											onCheckedChange={() => onToggleItemSelection(candidate.id)}
											aria-label={`Select ${candidate.name}`}
										/>
									</div>
								)}
								<div className="flex justify-between items-start w-full">
									<div className="flex-1 min-w-0">
										{candidate.jobId && !candidate.candidateId ? (
											<>
												<div className="flex items-center gap-2 mb-1">
													<Link 
														to={`/jobs/${candidate.jobId}`} 
														className={`font-semibold hover:underline group-hover:${statusColors.color} transition-colors duration-200 block truncate flex-1`}
														title={candidate.name}
														onClick={(e) => e.stopPropagation()}
													>
														{candidate.name}
													</Link>
													{candidate.published && (
														<Popover>
															<PopoverTrigger asChild>
																<button
																	className="text-green-600 hover:text-green-700 transition-colors flex-shrink-0"
																	type="button"
																	title="Published - Click for options"
																>
																	<Globe className="h-4 w-4" />
																</button>
															</PopoverTrigger>
															<PopoverContent className="w-60 p-3" side="bottom" align="start">
																<div className="space-y-3">
																	<div className="flex items-center gap-2 mb-3">
																		<Globe className="h-4 w-4 text-green-600" />
																		<span className="font-medium text-sm">Published Job</span>
																	</div>
																	<div className="space-y-2">
																		<button
																			className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
																			onClick={() => {
																				window.open(`${window.location.origin}/jobs/${candidate.jobId}/public`, '_blank', 'noopener');
																			}}
																		>
																			<Eye className="h-4 w-4 text-blue-500" />
																			<div>
																				<div className="text-sm font-medium">View Public Page</div>
																				<div className="text-xs text-gray-500 dark:text-gray-400">Open in new tab</div>
																			</div>
																		</button>
																		<button
																			className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
																			onClick={() => {
																				navigator.clipboard.writeText(`${window.location.origin}/jobs/${candidate.jobId}/public`);
																				toast({ 
																					title: 'Copied!', 
																					description: 'Public page URL copied to clipboard.' 
																				});
																			}}
																		>
																			<ClipboardCopy className="h-4 w-4 text-gray-600 dark:text-gray-300" />
																			<div>
																				<div className="text-sm font-medium">Copy Link</div>
																				<div className="text-xs text-gray-500 dark:text-gray-400">Share this job publicly</div>
																			</div>
																		</button>
																	</div>
																</div>
															</PopoverContent>
														</Popover>
													)}
												</div>
												{candidate.position && (
													<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" title={candidate.position}>
														{candidate.position}
													</div>
												)}
												{company && (
													<Link 
														to={candidate.companyId ? `/companies/${candidate.companyId}` : '#'}
														className={`text-xs mt-1 font-medium truncate block ${candidate.companyId ? 'text-gray-600 dark:text-gray-400 hover:underline cursor-pointer' : 'text-gray-600 dark:text-gray-400 cursor-default'}`}
														title={company}
														onClick={(e) => {
															if (!candidate.companyId) {
																e.preventDefault();
															}
															e.stopPropagation();
														}}
													>
														{company}
													</Link>
												)}
												{/* Applications & Processes counts */}
												<div className="flex flex-row gap-2 mt-2">
													<div
														className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-semibold"
														style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' }}
													>
														<span>{candidate.applicationsCount ?? 0}</span>
														<span className="ml-1">Applications</span>
													</div>
													<div
														className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-semibold"
														style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}
													>
														<span>{candidate.processesCount ?? 0}</span>
														<span className="ml-1">Processes</span>
													</div>
												</div>
											</>
										) : (
											<>
												<Link 
													to={pointOfView === "candidate" ? `/jobs/${candidate.jobId}` : `/candidates/${candidate.candidateId}`}
													className="font-medium hover:underline block truncate w-full"
													title={candidate.name}
													onClick={(e) => e.stopPropagation()}
												>
													{candidate.name}
												</Link>
												{candidate.position && (
													<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" title={candidate.position}>
														{candidate.position}
													</div>
												)}
												{jobTitle && (
													<Link 
														to={`/jobs/${candidate.jobId}`}
														className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate hover:underline cursor-pointer block"
														title={jobTitle}
														onClick={(e) => e.stopPropagation()}
													>
														{jobTitle}
													</Link>
												)}
												{company && (
													<Link 
														to={candidate.companyId ? `/companies/${candidate.companyId}` : '#'}
														className={`text-xs mt-0.5 block truncate ${candidate.companyId ? 'text-gray-400 dark:text-gray-500 hover:underline cursor-pointer' : 'text-gray-400 dark:text-gray-500 cursor-default'}`}
														title={company}
														onClick={(e) => {
															if (!candidate.companyId) {
																e.preventDefault();
															}
															e.stopPropagation();
														}}
													>
														{company}
													</Link>
												)}
											</>
										)}
										{createdAt && (
											<div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
												<span>Modified - {createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ({daysAgo === 0 ? 'today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`})</span>
											</div>
										)}
									</div>
									{candidate.score !== undefined && !candidate.analysisInProgress && candidate.score !== 0 && (
										<MatchScore score={candidate.score} size="md" />
									)}
									{(candidate.analysisInProgress || candidate.score === 0) && (
										<MatchScore score={candidate.score} running={true} size="md" />
									)}
								</div>
							</div>
						);
					})}
					{candidates.length === 0 && (
						<div className="h-20 flex flex-col items-center justify-center text-sm text-foreground/40 italic">
							<div className={`w-8 h-8 rounded-sm ${statusColors.bgColor} ${statusColors.borderColor} border-2 border-dashed mb-2 flex items-center justify-center`}>
								<span className={`text-xs ${statusColors.color}`}>0</span>
							</div>
							<span className="text-center">{t('kanban.noOffers')}</span>
						</div>
					)}
				</div>
			) : null}
		</div>
	);
};
